import { useState, useEffect, useMemo, useCallback } from 'react';
import { Send, CheckCircle2, AlertCircle, Save } from 'lucide-react';

const HERO_IMG =
  'https://customer-assets.emergentagent.com/job_gate33-preview/artifacts/kyukeuh3_65e0cd8d755b2661fafcedd4_65ca66f1e325b815020944eb_global-5000%2520EXT.webp';
const DISCOVERY_URL = 'https://calendly.com/nicolas-gate33/discovery-call';
const STORAGE_KEY = 'gate33_operational_assessment_v1';
const API_BASE = process.env.REACT_APP_BACKEND_URL;

const SCENARIOS = [
  { key: 'q_01', prompt: "Your aircraft departs in 24 hours for a two-week rotation across twelve cities. What are the three first things you do, once you're onboard?" },
  { key: 'q_02', prompt: "Your catering order has been cancelled two hours before departure. It's 6:30pm and you're in Limerick, Ireland. What do you do?" },
  { key: 'q_03', prompt: "On a multi-day trip with no home base, who manages laundry, restocking, and cabin upkeep between legs — and how? Give three options." },
  { key: 'q_04', prompt: "After speaking with a guest's assistant, she mentions the guest really likes James Bond. What do you do with that?" },
  { key: 'q_05', prompt: "How would you describe the difference between commercial and private aviation service, if asked in an interview?" },
  { key: 'q_06', prompt: "You'll be operating an ACJ 320. Do you know whether this requires you to hold a Cabin Crew Attestation? Explain your reasoning." },
  { key: 'q_07', prompt: "You're flying French wealthy guests on a three-hour flight. What menu are you going to propose?" },
  { key: 'q_08', prompt: "From the guest profile, you read the preferences and find nothing written. Once onboard, you hand her the menu and she tells you she only eats gluten free and vegan meals. You're still on the ground, departure is in 40 minutes, and you're at LFMN airport. You don't have gluten free products, just a few tomatoes onboard. What do you plan?" },
  { key: 'q_09', prompt: "A guest comes onboard with a bag full of wines and hands it to you on embarkation, asking to be served only these wines. On disembarkation, one bottle is left, but you forget to give it back. A few days later, the guest emails operations asking for his expensive wine bottle back. You've already left Miami and you're now in Paris. How do you handle it? What do you do? Who do you write to?" },
  { key: 'q_10', prompt: "You have a 50-minute flight and have ordered your catering, but one hour before takeoff you receive two catering orders: yours, and one placed by operations — except operations didn't inform you, and neither did the catering company. What do you do?" },
  { key: 'q_11', prompt: "You fly on the Bombardier Challenger 3500. What do you prepare?" },
  { key: 'q_12', prompt: "Can you tell us the operational differences for a flight attendant between a Challenger 3500 and a Global 7500?" },
];

const CASES = [
  {
    id: 'case_1', title: 'Case 1 — The Missing Wine',
    stages: [
      { key: 'case_1_stage_1', label: 'Stage 1', prompt: "The morning of departure, the principal's assistant requests a specific wine that isn't in your current stock. What do you do?" },
      { key: 'case_1_stage_2', label: 'Stage 2 (escalation)', prompt: "You sourced the wine. On arrival at the aircraft, the FBO handler breaks the bottle. What do you do now?" },
    ],
  },
  {
    id: 'case_2', title: 'Case 2 — Dominican Republic',
    stages: [
      { key: 'case_2_stage_1', label: 'Stage 1', prompt: "You're in the Dominican Republic and no catering company shows up in your app. What do you do?" },
      { key: 'case_2_stage_2', label: 'Stage 2 (escalation)', prompt: "You're still in the Dominican Republic. The FBO doesn't answer, the hotel can't do short notice, yesterday's leftovers are gone, and the supermarket has nothing prepared. What do you do now?" },
    ],
  },
  {
    id: 'case_3', title: 'Case 3 — Owner Flight, Le Bourget',
    stages: [
      { key: 'case_3_stage_1', label: 'Stage 1', prompt: "You've ordered everything for the round trip and you're waiting at Le Bourget for seven hours. On disembarking, the owner asks for something else: some sushi. What do you do?" },
      { key: 'case_3_stage_2', label: 'Stage 2 (escalation)', prompt: "The return flight was expected at 6pm, but at 2pm the owner calls to say he'll be there in ninety minutes. You've already ordered the sushi, but the caterer says it may run late, closer to 4pm. What do you do?" },
      { key: 'case_3_stage_3', label: 'Stage 3 (escalation)', prompt: "There's no delivery app coverage where you are, the nearest restaurant just closed, and you have forty-five minutes left to go, come back, and prepare something, for a twenty-five minute flight. How do you handle this?" },
    ],
  },
];

const CONTACT_FIELDS = [
  { key: 'first_name', label: 'First Name', required: true, type: 'text' },
  { key: 'last_name', label: 'Family Name', required: true, type: 'text' },
  { key: 'email', label: 'Email', required: true, type: 'email' },
  { key: 'phone', label: 'Phone Number', required: true, type: 'tel' },
  { key: 'position', label: 'Current Position', required: true, type: 'text' },
  { key: 'company', label: 'Company Name (optional)', required: false, type: 'text' },
];

const emptyState = () => ({
  first_name: '', last_name: '', email: '', phone: '', position: '', company: '',
  website: '',
  scenarios: Object.fromEntries(SCENARIOS.map((q) => [q.key, ''])),
  cases: Object.fromEntries(CASES.flatMap((c) => c.stages.map((s) => [s.key, '']))),
});

const OperationalAssessmentPage = () => {
  const [form, setForm] = useState(emptyState);
  const [status, setStatus] = useState({ state: 'idle' }); // idle | submitting | done | error
  const [savedAt, setSavedAt] = useState(null);
  const [showEmptyWarning, setShowEmptyWarning] = useState(false);

  // Load draft + fire assessment_started event
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setForm({ ...emptyState(), ...JSON.parse(raw) });
    } catch (e) { /* ignore */ }
    document.title = 'Operational Readiness Assessment · Gate 33';
    window.gtag?.('event', 'assessment_started');
    window.lintrk?.('track', { conversion_id: 'assessment_started' });
  }, []);

  // Auto-save
  useEffect(() => {
    if (status.state === 'done') return;
    const t = setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(form));
        setSavedAt(new Date());
      } catch (e) { /* ignore */ }
    }, 800);
    return () => clearTimeout(t);
  }, [form, status.state]);

  const setField = useCallback((k, v) => setForm((f) => ({ ...f, [k]: v })), []);
  const setScenario = useCallback((k, v) => setForm((f) => ({ ...f, scenarios: { ...f.scenarios, [k]: v } })), []);
  const setCase = useCallback((k, v) => setForm((f) => ({ ...f, cases: { ...f.cases, [k]: v } })), []);

  const totalAnswers = useMemo(() => {
    const s = Object.values(form.scenarios).filter((x) => (x || '').trim().length > 5).length;
    const c = Object.values(form.cases).filter((x) => (x || '').trim().length > 5).length;
    return s + c;
  }, [form.scenarios, form.cases]);
  const totalQuestions = SCENARIOS.length + CASES.reduce((n, c) => n + c.stages.length, 0);

  const onSubmit = async (e) => {
    e.preventDefault();
    // Contact validation
    for (const f of CONTACT_FIELDS) {
      if (f.required && !(form[f.key] || '').trim()) {
        setStatus({ state: 'error', msg: `${f.label} is required.` });
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    }
    // Soft warning on low-effort submissions
    if (totalAnswers < 3 && !showEmptyWarning) {
      setShowEmptyWarning(true);
      setStatus({ state: 'error', msg: `You've only answered ${totalAnswers} of ${totalQuestions} questions. Click Submit again to send anyway.` });
      return;
    }

    setStatus({ state: 'submitting' });
    try {
      const res = await fetch(`${API_BASE}/api/operational-assessment/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data.ok) throw new Error('Submission rejected');
      setStatus({ state: 'done' });
      try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* ignore */ }
      window.gtag?.('event', 'assessment_completed');
      window.lintrk?.('track', { conversion_id: 'assessment_completed' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setStatus({ state: 'error', msg: 'Submission failed. Please try again or email nicolas@gate33.co directly.' });
    }
  };

  // ===== DONE STATE =====
  if (status.state === 'done') {
    return (
      <div data-testid="assessment-done" className="min-h-screen bg-white">
        <section className="section-padding bg-[#FDFBF7] border-b border-[#E5E0D8]">
          <div className="container-custom max-w-3xl text-center">
            <div className="w-14 h-14 rounded-full border border-[#C5A880] flex items-center justify-center mx-auto mb-8">
              <CheckCircle2 size={26} className="text-[#C5A880]" strokeWidth={1.5} />
            </div>
            <span className="overline inline-block mb-4">Submission received</span>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mb-8 text-[#0F0F0F]">
              Thank you for completing this assessment.
            </h1>
            <p className="text-lg text-[#5A5A5A] leading-relaxed mb-4">
              Nicolas will review your answers personally and get back to you with honest feedback on where you stand, operationally, and what would close the gap if there is one.
            </p>
            <p className="text-[#5A5A5A] leading-relaxed mb-10">
              If you&apos;d rather talk it through now instead of waiting, you can book a free discovery call.
            </p>
            <a
              href={DISCOVERY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="assessment-done-cta"
              className="btn-primary inline-flex items-center gap-2"
            >
              Book a Free Discovery Call
            </a>
          </div>
        </section>
      </div>
    );
  }

  // ===== FORM =====
  return (
    <div data-testid="assessment-page" className="min-h-screen bg-white">
      {/* Hero — ivory overlay style consistent with Home / About */}
      <section data-testid="assessment-hero" className="relative min-h-[75vh] flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src={HERO_IMG}
            alt="Gate 33 Operational Readiness Assessment — private jet on tarmac"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FDFBF7] via-[#FDFBF7]/85 to-[#FDFBF7]/30" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24 w-full">
          <div className="max-w-2xl">
            <span className="overline inline-block mb-6">Operational Readiness Assessment</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#0F0F0F] mb-8">
              Tell us what you&apos;d actually do.
              <br />
              Not what sounds right.
            </h1>
            <p className="text-lg text-[#1A1A1A] leading-relaxed max-w-xl">
              Real scenarios pulled from actual rotations. No multiple choice, no perfect answers. Write what you&apos;d genuinely do — Nicolas reads every submission personally.
            </p>
          </div>
        </div>
      </section>

      <form onSubmit={onSubmit} noValidate>
        {/* Intro strip — the "how" explainer */}
        <section data-testid="assessment-intro" className="section-padding bg-white border-b border-[#E5E0D8]">
          <div className="container-custom max-w-3xl">
            <span className="overline inline-block mb-4">Before you begin</span>
            <h2 className="font-heading text-3xl sm:text-4xl leading-tight text-[#0F0F0F] mb-6">
              A genuine writing task — plan on 15–20 minutes.
            </h2>
            <p className="text-lg text-[#1A1A1A] leading-relaxed mb-4">
              Some scenarios get harder as they go. There&apos;s no multiple choice — <strong>write what you&apos;d genuinely do, in your own words.</strong>
            </p>
            <p className="text-sm text-[#5A5A5A] leading-relaxed">
              Your progress is auto-saved on this device. Nicolas reviews every submission personally.
            </p>
          </div>
        </section>

        {/* Error banner */}
        {status.state === 'error' && (
          <div data-testid="assessment-error" className="max-w-3xl mx-auto px-6 md:px-12 mb-8">
            <div className="flex items-start gap-3 border border-[#C5A880] bg-[#FDFBF7] p-4">
              <AlertCircle size={18} className="text-[#C5A880] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-[#1A1A1A] leading-relaxed">{status.msg}</p>
            </div>
          </div>
        )}

        {/* Contact fields */}
        <section data-testid="assessment-contact" className="section-padding bg-[#FDFBF7] border-y border-[#E5E0D8]">
          <div className="container-custom max-w-3xl">
            <span className="overline inline-block mb-4">Your details</span>
            <h2 className="font-heading text-2xl sm:text-3xl leading-tight text-[#0F0F0F] mb-8">Before we start.</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {CONTACT_FIELDS.map((f) => (
                <label key={f.key} className="block">
                  <span className="block text-xs uppercase tracking-[0.18em] font-semibold text-[#1A1A1A] mb-2">
                    {f.label}{f.required && <span className="text-[#C5A880]"> *</span>}
                  </span>
                  <input
                    type={f.type}
                    value={form[f.key]}
                    onChange={(e) => setField(f.key, e.target.value)}
                    required={f.required}
                    data-testid={`assessment-field-${f.key}`}
                    className="w-full border border-[#E5E0D8] bg-white px-4 py-3 text-[#0F0F0F] focus:border-[#C5A880] focus:outline-none transition-colors"
                  />
                </label>
              ))}
            </div>
            {/* Honeypot */}
            <div className="hidden" aria-hidden="true">
              <label>Website<input type="text" value={form.website} onChange={(e) => setField('website', e.target.value)} tabIndex={-1} autoComplete="off" /></label>
            </div>
          </div>
        </section>

        {/* Part One — editorial cards */}
        <section data-testid="assessment-part-one" className="section-padding bg-white">
          <div className="container-custom max-w-3xl">
            <div className="mb-14 text-center">
              <span className="overline inline-block mb-4">Part One</span>
              <h2 className="font-heading text-3xl sm:text-4xl leading-tight text-[#0F0F0F]">
                Scenarios.
              </h2>
              <p className="text-[#5A5A5A] mt-3">Twelve real situations. Answer each in your own words.</p>
            </div>
            <div className="space-y-8">
              {SCENARIOS.map((q, i) => (
                <article
                  key={q.key}
                  data-testid={`assessment-scenario-${q.key}`}
                  className="bg-white border border-[#E5E0D8] p-6 md:p-8 grid grid-cols-[auto_1fr] gap-5 md:gap-8"
                >
                  <div className="flex flex-col items-start md:items-center gap-1 md:pt-1">
                    <span
                      aria-hidden
                      className="font-heading text-4xl md:text-5xl text-[#C5A880] leading-none tracking-tight"
                    >
                      {String(i + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <div>
                    <p className="font-heading text-lg md:text-xl text-[#0F0F0F] leading-snug mb-4">
                      {q.prompt}
                    </p>
                    <textarea
                      value={form.scenarios[q.key]}
                      onChange={(e) => setScenario(q.key, e.target.value)}
                      placeholder="Aim for a few sentences."
                      rows={4}
                      data-testid={`assessment-scenario-input-${q.key}`}
                      className="w-full border border-[#E5E0D8] bg-[#FDFBF7] px-4 py-3 text-[#0F0F0F] focus:border-[#C5A880] focus:bg-white focus:outline-none transition-colors resize-y"
                    />
                    <div className="text-[10px] uppercase tracking-[0.15em] text-[#9A9A9A] mt-2 text-right">
                      {(form.scenarios[q.key] || '').trim().split(/\s+/).filter(Boolean).length} words
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Part Two — editorial case blocks */}
        <section data-testid="assessment-part-two" className="section-padding bg-[#FDFBF7] border-y border-[#E5E0D8]">
          <div className="container-custom max-w-3xl">
            <div className="mb-14 text-center">
              <span className="overline inline-block mb-4">Part Two</span>
              <h2 className="font-heading text-3xl sm:text-4xl leading-tight text-[#0F0F0F]">
                Case studies.
              </h2>
              <p className="text-[#5A5A5A] mt-3">
                Each case escalates. Read stages in sequence — your answer to one shapes the next.
              </p>
            </div>
            <div className="space-y-12">
              {CASES.map((c) => (
                <article
                  key={c.id}
                  data-testid={`assessment-case-${c.id}`}
                  className="bg-white border border-[#E5E0D8] p-8 md:p-10"
                >
                  <div className="flex items-baseline gap-4 pb-6 mb-8 border-b border-[#E5E0D8]">
                    <span className="font-heading text-2xl text-[#C5A880]">
                      {c.id.replace('case_', '')}
                    </span>
                    <h3 className="font-heading text-2xl text-[#0F0F0F] leading-tight">
                      {c.title.replace(/^Case \d+ — /, '')}
                    </h3>
                  </div>
                  <div className="space-y-8">
                    {c.stages.map((s, si) => (
                      <div key={s.key} className="grid grid-cols-[auto_1fr] gap-5">
                        <div className="w-8 flex flex-col items-center pt-1">
                          <span className="w-8 h-8 rounded-full border border-[#C5A880] flex items-center justify-center text-xs font-heading text-[#C5A880]">
                            {si + 1}
                          </span>
                          {si < c.stages.length - 1 && (
                            <span className="w-px flex-1 bg-[#E5E0D8] mt-2 min-h-[40px]" aria-hidden />
                          )}
                        </div>
                        <div className="pb-2">
                          <span className="text-[10px] uppercase tracking-[0.2em] text-[#C5A880] font-semibold">{s.label}</span>
                          <p className="font-heading text-lg text-[#0F0F0F] leading-snug mt-1 mb-4">{s.prompt}</p>
                          <textarea
                            value={form.cases[s.key]}
                            onChange={(e) => setCase(s.key, e.target.value)}
                            placeholder="Aim for a few sentences."
                            rows={4}
                            data-testid={`assessment-case-input-${s.key}`}
                            className="w-full border border-[#E5E0D8] bg-[#FDFBF7] px-4 py-3 text-[#0F0F0F] focus:border-[#C5A880] focus:bg-white focus:outline-none transition-colors resize-y"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Submit */}
        <section className="section-dark py-16">
          <div className="container-custom max-w-3xl text-center">
            <div className="flex items-center justify-center gap-2 text-xs uppercase tracking-[0.18em] text-[#C5A880] mb-6">
              <Save size={12} strokeWidth={1.8} />
              <span>{savedAt ? `Saved locally · ${savedAt.toLocaleTimeString()}` : 'Auto-saving as you type'}</span>
              <span className="text-[#5A5A5A]">·</span>
              <span className="text-[#5A5A5A]">{totalAnswers}/{totalQuestions} answered</span>
            </div>
            <button
              type="submit"
              disabled={status.state === 'submitting'}
              data-testid="assessment-submit"
              className="inline-flex items-center justify-center gap-2 bg-[#C5A880] text-[#0F0F0F] px-12 py-4 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#FDFBF7] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {status.state === 'submitting' ? 'Sending…' : 'Submit assessment'}
              {status.state !== 'submitting' && <Send size={14} />}
            </button>
            <p className="text-xs uppercase tracking-[0.18em] text-[#5A5A5A] mt-6">
              Nicolas will review your submission personally.
            </p>
          </div>
        </section>
      </form>
    </div>
  );
};

export default OperationalAssessmentPage;
